import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { MaterialIcon } from "@/components/MaterialIcon";
import { AvatarMenu } from "@/components/AvatarMenu";
import { SearchOverlay } from "@/components/SearchOverlay";
import { useWindowWidth } from "@/lib/useBreakpoint";

const NAV = [
  { to: "/", label: "Home", testId: "nav-home" },
  { to: "/performances", label: "Performances", testId: "nav-performances" },
  { to: "/competitions", label: "Competitions", testId: "nav-competitions" },
  { to: "/festivals", label: "Festivals", testId: "nav-festivals" },
  { to: "/styles", label: "Browse by dance style", testId: "nav-dance-styles" },
];

const NavPill = ({ to, label, testId, onClick, block = false }) => (
  <NavLink
    to={to}
    end={to === "/"}
    onClick={onClick}
    data-testid={testId}
    className={({ isActive }) =>
      `${block ? "block w-full" : ""} px-3 xl:px-5 py-2 rounded-full text-[14px] xl:text-[15px] font-semibold whitespace-nowrap transition-colors duration-200 ${
        isActive ? "bg-[#EDEDEB] text-black" : "text-black hover:bg-[#F3F3F1]"
      }`
    }
  >
    {label}
  </NavLink>
);

export const Header = () => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const width = useWindowWidth();
  const isMobile = width < 768;
  const expanded = searchOpen && isMobile;

  useEffect(() => {
    setSearchOpen(false);
    setMenuOpen(false);
    setQuery("");
  }, [location.pathname]);

  useEffect(() => {
    if (!searchOpen) setSearchFocused(false);
  }, [searchOpen]);

  useEffect(() => {
    if (!searchOpen) return;
    const onKey = (e) => e.key === "Escape" && setSearchOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchOpen]);

  return (
    <>
      {searchOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/45 backdrop-blur-[1px] transition-opacity"
          onClick={() => setSearchOpen(false)}
          data-testid="search-backdrop"
        />
      )}
      <div className="sticky top-0 z-40 bg-white rounded-b-[24px] md:rounded-b-[40px] shadow-[0_6px_24px_rgba(0,0,0,0.06)]" data-testid="site-header">
        <div className="mx-auto max-w-[1280px] 2xl:max-w-[1560px] 3xl:max-w-[1840px] 4xl:max-w-[2280px] px-4 sm:px-6 lg:px-6 xl:px-10 4xl:px-16 h-[68px] md:h-[84px] lg:h-[104px] 4xl:h-[124px] flex items-center gap-3 sm:gap-6 lg:gap-5 xl:gap-11">
          {!searchOpen && (
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className="lg:hidden flex items-center justify-center text-black"
              aria-label="Menu"
              data-testid="mobile-menu-button"
            >
              <MaterialIcon name={menuOpen ? "close" : "menu"} size={28} weight={500} />
            </button>
          )}
          {!expanded && (
            <NavLink to="/" className="shrink-0" data-testid="logo-link">
              <Logo size={32} className="text-[24px] md:text-[28px] lg:text-[32px]" />
            </NavLink>
          )}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2" data-testid="main-nav">
            {searchOpen ? <NavPill to="/" label="Home" testId="nav-home" /> : NAV.map((n) => <NavPill key={n.to} {...n} />)}
          </nav>
          {searchOpen && (
            <div className="flex-1 min-w-0 flex items-center gap-2 h-[44px] md:h-[54px] rounded-full border border-[#D6D6D6] bg-white px-4 md:px-6 df-panel-in" data-testid="search-bar">
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                className="shrink-0 flex items-center text-[#9AA0A6] hover:text-black transition-colors"
                aria-label="Close search"
                data-testid="search-close-button"
              >
                <MaterialIcon name="close" size={24} weight={600} />
              </button>
              <input
                autoFocus={!isMobile}
                value={query}
                onFocus={() => setSearchFocused(true)}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.target.blur();
                    if (query.trim()) navigate(`/search?q=${encodeURIComponent(query.trim())}`);
                  }
                }}
                placeholder="Search Dancerfloor"
                className="flex-1 min-w-0 bg-transparent outline-none text-[16px] font-medium placeholder:text-[#6F6F6F]"
                data-testid="search-input"
              />
              <MaterialIcon name="search" size={26} weight={600} className="text-black shrink-0" />
            </div>
          )}
          {!expanded && (
            <div className={`flex items-center gap-4 sm:gap-6 lg:gap-7 ${searchOpen ? "" : "ml-auto"}`}>
              {!searchOpen && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setSearchOpen(true);
                  }}
                  className="flex items-center justify-center text-[#9AA0A6] hover:text-black hover:scale-110 transition-[transform,color] duration-200"
                  aria-label="Search"
                  data-testid="search-button"
                >
                  <MaterialIcon name="search" size={30} weight={500} />
                </button>
              )}
              <AvatarMenu />
            </div>
          )}
        </div>
        {menuOpen && !searchOpen && (
          <nav className="lg:hidden border-t border-[#EDEDEB] px-4 py-3 space-y-1 df-panel-in" data-testid="mobile-nav">
            {NAV.map((n) => (
              <NavPill key={n.to} {...n} testId={`${n.testId}-mobile`} block onClick={() => setMenuOpen(false)} />
            ))}
          </nav>
        )}
        {searchOpen && <SearchOverlay query={query} onNavigate={() => setSearchOpen(false)} />}
      </div>
    </>
  );
};
